import loadScript from 'load-script';
import { mergeDeepLeft } from 'ramda';
import videojs, { VideoJsPlayer } from 'video.js';

import { defaultOptions } from '@src/constants/options';
import PlayerError, { ErrorMessage } from '@src/errors/PlayerError';
import { UserPlayerOptions, UserPlayerOptionsSchema } from '@src/types/player';
import type {
  NullableGenericError,
  VideoJsOnErrorCallback,
} from '@src/types/player';
import { getIsBrowser } from '@src/utils/device';
import logger from '@src/utils/logger';
import { createWeakRandomString } from '@src/utils/str';
import { getAndParseVcpInfo } from '@src/utils/vcp';

import Base from '../core/Base';

class CreatePlayerMixin extends Base {
  restoreContainerElement?: (restoredEl: Element) => void;

  protected processVideoElement(videoElement: HTMLElement) {
    if (!videoElement || videoElement?.tagName?.toUpperCase?.() !== 'VIDEO')
      return;
    videoElement.classList.add('video-js');
  }

  protected getContainerElement(elementOrId: string | HTMLElement) {
    return typeof elementOrId === 'string'
      ? document.getElementById(elementOrId)
      : elementOrId instanceof Element
      ? elementOrId
      : undefined;
  }

  protected getAndPrepareVideoElement(containerElement: HTMLElement) {
    let videoElement: HTMLVideoElement;
    let clonedContainerElement: Element | undefined = undefined;

    if (containerElement?.tagName?.toUpperCase() === 'VIDEO') {
      videoElement = containerElement as HTMLVideoElement;
    } else {
      clonedContainerElement = containerElement.cloneNode(true) as HTMLElement;
      while (containerElement?.firstChild) {
        containerElement.removeChild(containerElement.firstChild);
      }
      containerElement.setAttribute('data-vjs-player', '');

      videoElement = document.createElement('video');

      containerElement?.appendChild(videoElement);
    }

    return { videoElement, clonedContainerElement };
  }

  private processUserOptions(userOptions?: UserPlayerOptions) {
    const { eventTypesToTrack, ...userOptionsToDeepMerge } = userOptions ?? {};

    const options = {
      ...mergeDeepLeft(userOptionsToDeepMerge ?? {}, defaultOptions),
      ...(eventTypesToTrack && { eventTypesToTrack }),
    };

    return options;
  }

  private handleErrorBeforeCreatePlayer(
    onError?: VideoJsOnErrorCallback,
    err?: NullableGenericError
  ) {
    if (onError === logger.error) {
      onError(err, null);
    } else {
      logger.error(err);
      onError?.(err, null);
    }
  }

  // need to load google ima sdk first before initiating videojs player,
  // so that contrib-ads is ready for capture the loadstart event when autoplay
  private async loadImaSdk(onError: (err?: Error | PlayerError) => void) {
    if (getIsBrowser() && !window.google?.ima) {
      return Promise.race([
        new Promise<void>((resolve, reject) => {
          loadScript(
            'https://imasdk.googleapis.com/js/sdkloader/ima3.js',
            (err) => {
              if (err) {
                reject(err);
              }
              resolve();
            }
          );
        }),
        new Promise<void>((resolve, reject) => {
          setTimeout(() => {
            reject(new Error('timeout to load the sdk script'));
          }, 3500);
        }),
      ]).catch((err) => {
        onError?.(
          new PlayerError(ErrorMessage.FAIL_TO_LOAD_GOOGLE_IMA_SDK, {
            cause: err,
          })
        );
      });
    }
  }

  public async createPlayer(
    containerElementOrId: string | HTMLElement | HTMLVideoElement,
    userOptions?: UserPlayerOptions,
    ready?: videojs.ReadyCallback
  ) {
    const options = this.processUserOptions(userOptions);

    const onError = (err?: NullableGenericError) =>
      this.handleErrorBeforeCreatePlayer(options?.onError, err);

    const parsed = UserPlayerOptionsSchema.safeParse(options);
    if (!parsed.success) {
      const _error = new PlayerError(
        `Invalid player options: ${containerElementOrId}`,
        { cause: parsed.error.format() }
      );

      onError(_error);
      // TODO invalid options UI
      return;
    }

    const parsedOptions = parsed.data;

    const { videoId = parsedOptions?.videoID, video } = parsedOptions;

    if (!videoId && !video) {
      logger.warn(
        `Empty videoId or video object: ${
          typeof containerElementOrId === 'string'
            ? containerElementOrId
            : containerElementOrId?.getAttribute('id')
        }`
      );
    }

    const [parsedInfo] = await Promise.all([
      getAndParseVcpInfo({
        videoIdOrObject: video ?? videoId,
        mediaServerURL: parsedOptions.mediaServerURL as string,
        onError,
      }),
      this.loadImaSdk(onError),
    ]);

    if (!parsedInfo?.src) {
      logger.warn(`Empty src parsed: ${JSON.stringify(parsedInfo)}`);
    }

    const containerElement = this.getContainerElement(containerElementOrId);

    if (!containerElement) {
      onError(
        new PlayerError(
          `Fail to locate contianer element: ${containerElementOrId}`
        )
      );
      // TODO empty container UI
      return;
    }

    const { videoElement, clonedContainerElement } =
      this.getAndPrepareVideoElement(containerElement);

    const restoreContainerElement = (_element: Element) => {
      if (clonedContainerElement) {
        _element?.parentNode?.replaceChild(clonedContainerElement, _element);
      }
    };

    this.processVideoElement(videoElement);

    const optionsToVjs = {
      ...options, // still pass options instead of parsedOptions so can pass extra options
      videoId, // pass videoId explicitely so options to videojs should not need to distinguish between videoId and videoID
    };

    const vjsPlayer = videojs(
      containerElement.tagName.toUpperCase() === 'video-js'
        ? containerElement
        : videoElement,
      {
        id:
          typeof containerElementOrId === 'string'
            ? containerElementOrId
            : containerElement.getAttribute('id') ??
              `vjs-hwvp-${createWeakRandomString()}`,
        language: 'zh-TW',
        ...optionsToVjs,
        ...(parsedInfo?.src && {
          sources: [
            {
              src: parsedInfo.src,
              type: 'application/x-mpegURL',
            },
          ],
          poster: parsedInfo.poster,
          title: parsedInfo.title,
          description: parsedInfo.description,
        }),
      },
      ready
    ) as VideoJsPlayer;

    return { vjsPlayer, clonedContainerElement, restoreContainerElement };
  }

  public disposePlayer(vjsPlayer: VideoJsPlayer) {
    if (!vjsPlayer) return;

    vjsPlayer.dispose();

    const playerRestoredEl =
      typeof vjsPlayer.options_.restoreEl === 'string'
        ? document.getElementById(vjsPlayer.options_.restoreEl)
        : vjsPlayer.options_.restoreEl instanceof Element
        ? vjsPlayer.options_.restoreEl
        : undefined;

    if (playerRestoredEl) {
      this.restoreContainerElement?.(playerRestoredEl);
    }
  }
}

export default CreatePlayerMixin;
